import {
  useState
} from 'react'
import {
  OnboardingWizardEvents,
  trackEvent
} from '../../analytics/posthog'

interface PermissionsDeferOptionProps {
  onDefer: () => void
  assistantName: string
  // The 'test' arm of the copy experiment (GET-19510): the trade-off waits
  // behind a quiet "Not now". Control keeps it open, in its original words.
  collapsed: boolean
}

// The forward path out of the permissions step for someone who does not want to
// grant mail/calendar access right now.
//
// Without it the step is a dead end: the wizard derives it from server state,
// so a user who declines on Google's screen is returned to the same question on
// every mount — including straight after connecting a channel, which is the
// harder ask they had just said yes to. Production showed users looping here
// and leaving rather than granting.
//
// The trade is stated before the deferring click, not after: Catch really does
// run without these scopes (the backend provisions a scope-less assistant and
// nudges on-channel later), it just cannot touch mail or calendar. The 'test'
// arm of GET-19510 puts it behind a quiet "Not now", on the theory that the
// always-open version reads as a list of reasons to skip (~66% skip share).
export function PermissionsDeferOption({
  onDefer,
  assistantName,
  collapsed,
}: PermissionsDeferOptionProps): JSX.Element {
  const [open, setOpen] = useState(false)

  if (!collapsed) {
    return (
      <div className="mt-6 pt-5 border-t border-gray-200 text-center">
        <p className="text-sm text-gray-500 leading-relaxed max-w-md mx-auto">
          Not ready to connect Google? Catch can still chat with you, set reminders, and take on
          research and tasks on the web. It just won&rsquo;t see your inbox or your calendar, so it
          can&rsquo;t schedule for you yet.
        </p>
        <button
          type="button"
          onClick={onDefer}
          className="mt-3 text-sm font-semibold text-gray-600 underline underline-offset-4 hover:text-gray-900 transition-colors"
        >
          Set this up later
        </button>
        <p className="text-xs text-gray-400 mt-2">
          You can connect Google any time in Settings.
        </p>
      </div>
    )
  }

  const handleOpen = (): void => {
    setOpen(true)
    trackEvent(OnboardingWizardEvents.PERMISSIONS_DEFER_OPENED)
  }

  if (!open) {
    return (
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={handleOpen}
          className="text-sm text-gray-500 hover:text-gray-800 underline underline-offset-4 transition-colors"
        >
          Not now
        </button>
      </div>
    )
  }

  return (
    <div className="mt-6 pt-5 border-t border-gray-200 text-center">
      <p className="text-sm text-gray-600 leading-relaxed max-w-md mx-auto">
        Without Mail &amp; Calendar, {assistantName} can still chat, set reminders and research
        things for you, but can&rsquo;t schedule meetings or help with your inbox.
      </p>
      <button
        type="button"
        onClick={onDefer}
        className="mt-3 text-sm font-semibold text-gray-600 underline underline-offset-4 hover:text-gray-900 transition-colors"
      >
        Continue without them
      </button>
      <p className="text-xs text-gray-400 mt-2">
        You can connect them any time in Settings.
      </p>
    </div>
  )
}
